import { z } from 'zod'

import {
  expenseCategorySchema,
  expenseScopeSchema,
  expenseSideSchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  labelSchema,
  moneyPaisa,
  noteSchema,
  positivePaisaSchema,
  ratioSchema,
  searchQuerySchema,
  sortSchema,
  versionSchema,
  yearMonthSchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * A27–A29 · Expenses.
 *
 * `animalId` absent means the bill is **shared**: rent, labour, a truck of
 * wanda. It is never divided evenly — it is allocated by animal-days over the
 * bill's own period (§4.4), so an animal bought on the 20th carries ten days of
 * the month's rent and not a thirtieth of it.
 */

export const expenseSchema = z.object({
  id: idSchema,
  scope: expenseScopeSchema,
  animalId: idSchema.nullish(),
  animalLabel: z.string().nullish(),
  category: expenseCategorySchema,
  side: expenseSideSchema,
  /**
   * Optional like every money key, and additionally subject to the Staff rule
   * in §4.16: a Staff member sees the amount of an expense **they** entered,
   * and only on the day they entered it, so they can check their own typing.
   */
  amountPaisa: moneyPaisa(),
  incurredOn: isoDateSchema,
  /** A shared bill's span. Inclusive on the wire; half-open internally. */
  periodStart: isoDateSchema.nullish(),
  periodEnd: isoDateSchema.nullish(),
  note: noteSchema.nullish(),
  billMediaId: idSchema.nullish(),
  /** Set when this row was derived from a lot cost split (§4.13). */
  lotCostId: idSchema.nullish(),
  /** Set when a monthly template posted it (§4.14). */
  recurringPostingId: idSchema.nullish(),
  /** True for the bulk-feed categories: this bill switches the estimate off. */
  suppressesFeedEstimate: z.boolean(),
  createdByName: z.string().nullish(),
  createdAt: isoInstantSchema,
  version: versionSchema,
})

/* ── A27 · GET /expenses ──────────────────────────────────────────────────── */

export const EXPENSE_SORTS = ['-incurredOn', 'incurredOn', '-amount', 'category'] as const

export const expenseListQuery = cursorQuerySchema.extend({
  /** `YYYY-MM`. Screen 12 is a month at a time. */
  month: yearMonthSchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  animalId: idSchema.optional(),
  category: expenseCategorySchema.optional(),
  scope: expenseScopeSchema.optional(),
  q: searchQuerySchema.optional(),
  sort: sortSchema(EXPENSE_SORTS, '-incurredOn'),
})

/**
 * Screen 12 is a month, not a list: the header ("Rs 2,87,500 · Rs 532 per
 * animal per day") and the split by category are the reason the trader opened
 * it, and they cannot be summed from one page of a cursor. So the response
 * carries the month's `summary` beside the paginated `items`, and
 * `meta.nextCursor` still pages `items` alone.
 */
export const expenseMonthSummarySchema = z.object({
  month: yearMonthSchema,
  totalPaisa: moneyPaisa(),
  /** Total ÷ animal-days: the real cost of holding one animal for one day. */
  perAnimalPerDayPaisa: moneyPaisa(),
  animalDays: z.number().int().nonnegative(),
  split: z.array(
    z.object({
      category: expenseCategorySchema,
      label: labelSchema,
      amountPaisa: moneyPaisa(),
      /** 0–1. Present even when the amount is not, so the bar still reads. */
      share: ratioSchema,
    }),
  ),
})

export const expenseListResponse = z.object({
  summary: expenseMonthSummarySchema.optional(),
  items: z.array(expenseSchema),
})

/* ── A28 · POST /expenses ─────────────────────────────────────────────────── */

/**
 * `scope` and `animalId` must agree, and the pairing is checked here rather
 * than left to the handler: a row with `scope: 'herd'` and an `animalId` is
 * ambiguous about whether it should be allocated, and allocation is the
 * difference between a cost basis that ties out and one that does not.
 */
export const createExpenseRequest = z
  .object({
    scope: expenseScopeSchema,
    animalId: idSchema.optional(),
    category: expenseCategorySchema,
    amountPaisa: positivePaisaSchema,
    incurredOn: isoDateSchema,
    periodStart: isoDateSchema.optional(),
    periodEnd: isoDateSchema.optional(),
    note: noteSchema.optional(),
    billMediaId: idSchema.optional(),
    /** Rarely sent: the category implies the side. Overridable for TRANSPORT. */
    side: expenseSideSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.scope === 'animal' && !v.animalId) {
      ctx.addIssue({
        code: 'custom',
        path: ['animalId'],
        message: 'Choose the animal this is for.',
      })
    }
    if (v.scope === 'herd' && v.animalId) {
      ctx.addIssue({
        code: 'custom',
        path: ['animalId'],
        message: 'A shared cost is not against one animal.',
      })
    }
    if (v.periodStart && v.periodEnd && v.periodEnd < v.periodStart) {
      ctx.addIssue({
        code: 'custom',
        path: ['periodEnd'],
        message: 'The period ends before it starts.',
      })
    }
    if (v.periodEnd && !v.periodStart) {
      ctx.addIssue({
        code: 'custom',
        path: ['periodStart'],
        message: 'A period needs a start date.',
      })
    }
  })

/* ── A29 · PATCH /expenses/:id · DELETE ───────────────────────────────────── */

/**
 * Editing an amount retroactively changes every animal's share of it, which is
 * correct and is why it is audited with before/after. A row derived from a lot
 * split (`lotCostId`) is edited through A39 instead, so the split cannot be
 * left inconsistent with its siblings.
 */
export const patchExpenseRequest = z
  .object({
    category: expenseCategorySchema.optional(),
    amountPaisa: positivePaisaSchema.optional(),
    incurredOn: isoDateSchema.optional(),
    periodStart: isoDateSchema.nullish(),
    periodEnd: isoDateSchema.nullish(),
    note: noteSchema.nullish(),
    billMediaId: idSchema.nullish(),
    animalId: idSchema.nullish(),
    scope: expenseScopeSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

export type Expense = z.infer<typeof expenseSchema>
export type ExpenseListQuery = z.input<typeof expenseListQuery>
export type ExpenseListResponse = z.infer<typeof expenseListResponse>
export type ExpenseMonthSummary = z.infer<typeof expenseMonthSummarySchema>
export type CreateExpenseRequest = z.input<typeof createExpenseRequest>
export type PatchExpenseRequest = z.input<typeof patchExpenseRequest>
